import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowUp,
  BookOpen,
  Briefcase,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Download,
  Edit2,
  FileText,
  Menu,
  MessageSquare,
  Mic,
  MicOff,
  MoreHorizontal,
  Paperclip,
  PhoneOff,
  Plus,
  Settings,
  Star,
  Tool as ToolIcon,
  Trash2,
  X,
  XCircle,
} from 'react-feather';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import PageContainer from '../components/PageContainer';
import { API_BASE_URL } from '../config/api';
import 'highlight.js/styles/github.css';
import './BusinessAssistants.css';
import { UserContext } from '../UserContext';
import {
  MIC_PERMISSION_STATE,
  buildMicRecoverySteps,
  requestMicrophoneAccess,
  stopStream,
} from '../utils/micPermission';
import {
  buildTableFilename,
  downloadCsv,
  downloadXlsx,
  extractTableRows,
  hasExportableData,
} from '../utils/tableExport';

const OUTPUT_SAMPLE_RATE = 24000;
const LIVE_TRANSCRIPT_KEY = 'live';

const VISION_UNSUPPORTED_MESSAGE =
  'This assistant’s model cannot read images. Switch it to a vision-capable model to attach images.';

const DOCUMENT_ACCEPT = 'application/pdf,.docx,text/plain';
const IMAGE_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';

// Noise gate removed: OpenAI server VAD (threshold: 0.50, silence_duration_ms: 500)
// is responsible for detecting end-of-turn.  Dropping silent audio frames
// client-side prevents server VAD from counting the silence it needs, so the
// model never commits the audio buffer and never generates a response until the
// next burst of audio arrives.  All audio (speech and silence) must be forwarded.

const buildRealtimeWsUrl = (path, token, conversationId) => {
  const httpUrl = new URL(path, API_BASE_URL);
  httpUrl.protocol = httpUrl.protocol === 'https:' ? 'wss:' : 'ws:';
  httpUrl.searchParams.set('token', token);
  if (conversationId) {
    httpUrl.searchParams.set('conversation_id', conversationId);
  }
  return httpUrl.toString();
};

const resampleFloat32 = (input, sourceSampleRate, targetSampleRate) => {
  if (sourceSampleRate === targetSampleRate) {
    return input;
  }

  const sampleRateRatio = sourceSampleRate / targetSampleRate;
  const outputLength = Math.max(1, Math.round(input.length / sampleRateRatio));
  const output = new Float32Array(outputLength);

  let outputIndex = 0;
  let inputIndex = 0;
  while (outputIndex < outputLength) {
    const nextInputIndex = Math.min(
      input.length,
      Math.round((outputIndex + 1) * sampleRateRatio)
    );

    let sum = 0;
    let count = 0;
    for (let index = inputIndex; index < nextInputIndex; index += 1) {
      sum += input[index];
      count += 1;
    }

    output[outputIndex] = count > 0 ? sum / count : input[inputIndex] || 0;
    outputIndex += 1;
    inputIndex = nextInputIndex;
  }

  return output;
};

const floatToPcm16 = (float32) => {
  const pcm16 = new Int16Array(float32.length);
  for (let index = 0; index < float32.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, float32[index]));
    pcm16[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return pcm16;
};

const pcm16ToFloat32 = (buffer) => {
  const pcm16 = new Int16Array(buffer);
  const output = new Float32Array(pcm16.length);
  for (let index = 0; index < pcm16.length; index += 1) {
    output[index] = pcm16[index] / 0x8000;
  }
  return output;
};

const normalizeMessageRole = (role) => {
  const normalized = (role || 'assistant').toLowerCase();
  if (normalized === 'agent' || normalized === 'bot') {
    return 'assistant';
  }
  return normalized;
};

const buildSeedConversation = (greeting) =>
  greeting ? [{ role: 'assistant', content: greeting, timestamp: null, seed: true }] : [];

const formatMessageTime = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatHistoryTime = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const now = new Date();
  const sameDay = now.toDateString() === date.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
  });
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) {
    return '';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageMediaType = (mediaType) =>
  typeof mediaType === 'string' && mediaType.startsWith('image/');

// Kept in sync with .gptConvMenu's width, which widens at the 640px breakpoint.
const CONV_MENU_WIDTH_DESKTOP = 190;
const CONV_MENU_WIDTH_MOBILE = 210;
const CONV_MENU_HEIGHT = 150;
const CONV_MENU_GAP = 6;
const CONV_MENU_EDGE = 8;

// The menu renders in a portal at viewport coordinates, so it escapes the
// sidebar's scroll clipping and the row's hover transform. It drops below the
// trigger, flipping above only when the bottom of the viewport is in the way.
const buildConvMenuPosition = (trigger) => {
  const rect = trigger.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom - CONV_MENU_GAP;
  const spaceAbove = rect.top - CONV_MENU_GAP;
  const flipUp = spaceBelow < CONV_MENU_HEIGHT && spaceAbove > spaceBelow;

  const width =
    window.innerWidth <= 640 ? CONV_MENU_WIDTH_MOBILE : CONV_MENU_WIDTH_DESKTOP;
  const maxLeft = window.innerWidth - width - CONV_MENU_EDGE;
  const left = Math.max(CONV_MENU_EDGE, Math.min(rect.right - width, maxLeft));

  return {
    left,
    top: flipUp ? undefined : rect.bottom + CONV_MENU_GAP,
    bottom: flipUp ? window.innerHeight - rect.top + CONV_MENU_GAP : undefined,
    maxHeight: Math.max(CONV_MENU_HEIGHT, (flipUp ? spaceAbove : spaceBelow) - CONV_MENU_EDGE),
  };
};

const buildConversationTitle = (conversation) => {
  if (!conversation) {
    return 'Conversation';
  }

  // A user-supplied name always wins over the auto-derived preview.
  if (conversation.title) {
    return conversation.title;
  }

  if (conversation.preview) {
    return conversation.preview;
  }

  // We keep sources distinct in the DB for billing/transcripts, but in the UI
  // we want them to appear as the same log/category.
  if (conversation.source === 'realtime_voice' || conversation.source === 'business_chat') {
    return 'Business Assistant';
  }

  return 'Conversation';
};

const requestHeaders = () => {
  const token = localStorage.getItem('access_token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

// Plugin instances are stable, so define them once at module scope rather than
// re-creating them on every render.
const MARKDOWN_REMARK_PLUGINS = [remarkGfm];
// `detect: false` + `ignoreMissing: true` keeps highlight.js from guessing a
// language for plain fenced blocks and from throwing on unknown languages.
const MARKDOWN_REHYPE_PLUGINS = [[rehypeHighlight, { detect: true, ignoreMissing: true }]];

// Wraps every GFM table the assistant produces with a toolbar offering CSV and
// Excel downloads. The rows are read off the hast node react-markdown already
// built, so what gets exported is exactly what is rendered, no second parse of
// the markdown source.
//
// The toolbar is a persistent strip rather than a hover affordance: hover has no
// equivalent on touch, and this workspace is used on mobile.
const DownloadableTable = ({ node, children, ...props }) => {
  const [busy, setBusy] = useState(null);
  const [failed, setFailed] = useState(false);

  const rows = useMemo(() => extractTableRows(node), [node]);
  const exportable = hasExportableData(rows);

  const handleDownload = async (format) => {
    setBusy(format);
    setFailed(false);
    try {
      const filename = buildTableFilename('table', 0, format === 'xlsx' ? 'xlsx' : 'csv');
      if (format === 'xlsx') {
        await downloadXlsx(rows, filename);
      } else {
        downloadCsv(rows, filename);
      }
    } catch {
      // The most likely cause is the lazy xlsx chunk failing to load; surface it
      // inline rather than leaving the button spinning.
      setFailed(true);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="gptTableBlock">
      {exportable && (
        <div className="gptTableToolbar">
          <span className="gptTableToolbarMeta">
            {rows.length} {rows.length === 1 ? 'row' : 'rows'}
          </span>
          <div className="gptTableToolbarActions">
            <button
              type="button"
              className="gptTableDownloadBtn"
              onClick={() => handleDownload('csv')}
              disabled={busy !== null}
              title="Download this table as CSV"
            >
              <Download size={13} />
              {busy === 'csv' ? 'Preparing…' : 'CSV'}
            </button>
            <button
              type="button"
              className="gptTableDownloadBtn"
              onClick={() => handleDownload('xlsx')}
              disabled={busy !== null}
              title="Download this table as an Excel workbook"
            >
              <Download size={13} />
              {busy === 'xlsx' ? 'Preparing…' : 'Excel'}
            </button>
          </div>
        </div>
      )}
      {failed && (
        <p className="gptTableDownloadError">
          Could not prepare the download. Please try again.
        </p>
      )}
      <div className="gptTableScroll">
        <table {...props}>{children}</table>
      </div>
    </div>
  );
};

// Component map is defined once at module scope so react-markdown does not see a
// new object identity on every render (which would remount every table and drop
// the toolbar's local state mid-download).
const MARKDOWN_COMPONENTS = {
  a: ({ node, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  table: DownloadableTable,
};

// Renders message content as GitHub-flavored markdown with syntax-highlighted
// code blocks. Links open in a new tab. react-markdown escapes raw HTML by
// default (no rehype-raw), so untrusted message content stays XSS-safe.
const MarkdownContent = React.memo(({ content }) => (
  <div className="gptMsgMarkdown">
    <ReactMarkdown
      remarkPlugins={MARKDOWN_REMARK_PLUGINS}
      rehypePlugins={MARKDOWN_REHYPE_PLUGINS}
      components={MARKDOWN_COMPONENTS}
    >
      {content}
    </ReactMarkdown>
  </div>
));

// Renders whatever the backend put in tool_data as readable text. Arguments and
// results arrive as objects on some tools and pre-stringified JSON on others,
// so both shapes have to survive without throwing on a circular or non-JSON
// value, one malformed call must not blank out the transcript.
const formatToolPayload = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

// Collapsible tool/knowledge call, matching the Logs view: icon, kind,
// monospace name, status pill, chevron; expanding reveals the arguments and
// result the backend already returns in `tool_data` but which the previous flat
// badge had nowhere to show.
const GptToolCallRow = ({ message, role }) => {
  const [open, setOpen] = useState(false);

  const toolData = message.tool_data || {};
  // Chat responses mark only explicit failures, so absence of a status means
  // the call completed, do not tighten this to === 'success'.
  const succeeded = toolData.status !== 'failed';
  const isKnowledge = role === 'knowledge_call';
  const kind = isKnowledge ? 'Knowledge' : 'Tool';
  const name = toolData.name || message.content || 'unknown';

  const args = formatToolPayload(
    toolData.arguments ?? toolData.input ?? toolData.parameters
  );
  const result = formatToolPayload(
    toolData.result ?? toolData.output ?? toolData.response
  );
  const errorText = formatToolPayload(toolData.error);
  const hasDetail = Boolean(args || result || errorText);

  return (
    <div className="gptToolRow">
      <div className="gptToolCall">
        <button
          type="button"
          className="gptToolHeader"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          disabled={!hasDetail}
          title={hasDetail ? undefined : 'No arguments or result were recorded for this call'}
        >
          <span className="gptToolIcon" aria-hidden="true">
            {isKnowledge ? <BookOpen size={14} /> : <ToolIcon size={14} />}
          </span>
          <span className="gptToolKind">{kind}</span>
          {/* Shown verbatim. Long names ellipsis rather than wrap so the row
              stays one line; title exposes the full value on hover. */}
          <span className="gptToolName" title={name}>
            {name}
          </span>
          <span className="gptToolSpacer" />
          <span
            className={`gptToolBadge ${
              succeeded ? 'gptToolBadge--ok' : 'gptToolBadge--fail'
            }`}
          >
            {succeeded ? <CheckCircle size={11} /> : <XCircle size={11} />}
            {succeeded ? 'Completed' : 'Failed'}
          </span>
          {hasDetail && (
            <span
              className={`gptToolChevron${open ? ' gptToolChevron--open' : ''}`}
              aria-hidden="true"
            >
              <ChevronDown size={14} />
            </span>
          )}
        </button>

        {open && hasDetail && (
          <div className="gptToolBody">
            {args && (
              <div>
                <div className="gptToolFieldLabel">Arguments</div>
                <pre className="gptToolCode">{args}</pre>
              </div>
            )}
            {result && (
              <div>
                <div className="gptToolFieldLabel">Result</div>
                <pre className="gptToolCode">{result}</pre>
              </div>
            )}
            {errorText && (
              <div className="gptToolError">
                <div className="gptToolFieldLabel">Error</div>
                <pre className="gptToolCode">{errorText}</pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const BusinessAssistants = () => {
  const { userEmail } = useContext(UserContext);
  const navigate = useNavigate();
  const { assistantId: routeAssistantId } = useParams();
  const isChatPage = Boolean(routeAssistantId);

  const [assistants, setAssistants] = useState([]);
  const [activeConversationIds, setActiveConversationIds] = useState({});
  const [conversationsByAssistant, setConversationsByAssistant] = useState({});
  const [conversationMessages, setConversationMessages] = useState({});
  const [draftMessagesByAssistant, setDraftMessagesByAssistant] = useState({});
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [voiceConnected, setVoiceConnected] = useState(false);
  const [voiceConnecting, setVoiceConnecting] = useState(false);
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState('Idle');
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Microphone permission recovery sheet. `micBlock` is null when there is
  // nothing to recover from; otherwise it holds the classified failure so the
  // sheet can show the right copy and the right steps.
  const [micBlock, setMicBlock] = useState(null);
  // Known-blocked mic (Chromium-family only) so the button can warn before the
  // user taps. null means "unknown", which is the correct default everywhere
  // permissions.query does not support the microphone name.
  const [micPermissionState, setMicPermissionState] = useState(null);
  // Conversation row controls: which row's "..." menu is open, which row is
  // being renamed inline, and which row is awaiting delete confirmation.
  const [openConversationMenuId, setOpenConversationMenuId] = useState(null);
  const [conversationMenuPos, setConversationMenuPos] = useState(null);
  const [renamingConversationId, setRenamingConversationId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [pendingDeleteConversationId, setPendingDeleteConversationId] = useState(null);

  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const playbackSourcesRef = useRef([]);
  const playbackCursorRef = useRef(0);
  const mediaStreamRef = useRef(null);
  const mediaSourceRef = useRef(null);
  const processorNodeRef = useRef(null);
  const monitorGainRef = useRef(null);
  const transcriptBuffersRef = useRef({});
  const activeAssistantRef = useRef(null);
  const voiceConversationIdRef = useRef(null);
  const voiceAssistantIdRef = useRef(null);
  const voiceResumedRef = useRef(false);
  const activeConversationIdsRef = useRef({});
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  // Greeting gate, mic is not started until the assistant's first audio AND
  // first transcript both arrive (or a grace timer fires if only one arrives).
  // greetingResolverRef holds the resolve() of the Promise that handleStartVoice
  // awaits.  audioArrivedRef / transcriptArrivedRef track which signals we have
  // received so far.  partialGreetingTimerRef is the grace timer started when
  // only one of the two signals has arrived.
  const audioArrivedRef = useRef(false);
  const transcriptArrivedRef = useRef(false);
  const greetingResolverRef = useRef(null);
  const partialGreetingTimerRef = useRef(null);
  // Mic stream captured during the pre-flight permission check, handed to
  // startMicrophoneCapture() so we never call getUserMedia twice per session.
  const pendingMicStreamRef = useRef(null);

  useEffect(() => {
    activeConversationIdsRef.current = activeConversationIds;
  }, [activeConversationIds]);

  // Track the microphone grant so the UI can warn before the user taps, and so
  // the recovery sheet closes itself the moment the user fixes the permission
  // in browser/OS settings. Only Chromium-family browsers implement
  // permissions.query({name:'microphone'}); elsewhere the state stays null
  // ("unknown") and the tap-time request remains the only signal.
  useEffect(() => {
    let status = null;
    let cancelled = false;

    const applyState = (nextState) => {
      if (cancelled) {
        return;
      }
      setMicPermissionState(nextState);
      if (nextState === 'granted') {
        setMicBlock(null);
      }
    };

    const handleChange = () => applyState(status?.state || null);

    navigator.permissions
      ?.query({ name: 'microphone' })
      .then((result) => {
        if (cancelled) {
          return;
        }
        status = result;
        applyState(result.state);
        result.addEventListener('change', handleChange);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      status?.removeEventListener('change', handleChange);
    };
  }, []);

  const selectedAssistant = useMemo(
    () => assistants.find((assistant) => assistant.id === routeAssistantId) || null,
    [assistants, routeAssistantId]
  );

  const activeConversationId = selectedAssistant
    ? activeConversationIds[selectedAssistant.id] || null
    : null;

  const supportsVision = Boolean(selectedAssistant?.supports_vision);

  const selectedConversations = useMemo(() => {
    if (!selectedAssistant) {
      return [];
    }

    return conversationsByAssistant[selectedAssistant.id] || [];
  }, [conversationsByAssistant, selectedAssistant]);

  const activeMessages = useMemo(() => {
    if (!selectedAssistant) return [];

    if (activeConversationId) {
      return conversationMessages[activeConversationId] || [];
    }

    const draft = draftMessagesByAssistant[selectedAssistant.id];
    return draft?.length
      ? draft
      : buildSeedConversation(
          selectedAssistant.placeholder || selectedAssistant.greeting
        );
  }, [activeConversationId, conversationMessages, draftMessagesByAssistant, selectedAssistant]);
  
  const displayedMessages = useMemo(() => {
    return [...activeMessages].sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;

      if (a.live && b.live) {
        const sameMoment = Math.abs(aTime - bTime) < 1500;
        if (sameMoment && a.role !== b.role) {
          if (a.role === 'user') return -1;
          if (b.role === 'user') return 1;
        }
      }

      if (aTime !== bTime) {
        return aTime - bTime;
      }

      return 0;
    });
  }, [activeMessages]);

  const groupedConversations = useMemo(() => {
    if (!selectedConversations.length) {
      return {};
    }

    // 'Starred' is a pinned section above the date buckets, mirroring how
    // Claude surfaces starred chats.
    const order = ['Starred', 'Today', 'Yesterday', 'This week', 'This month', 'Older'];
    const now = new Date();
    const buckets = {};

    const sortedConversations = [...selectedConversations].sort((a, b) => {
      const aTime = new Date(a.end_time || a.start_time || 0).getTime();
      const bTime = new Date(b.end_time || b.start_time || 0).getTime();
      return bTime - aTime;
    });

    sortedConversations.forEach((conversation) => {
      if (conversation.is_starred) {
        if (!buckets.Starred) {
          buckets.Starred = [];
        }
        buckets.Starred.push(conversation);
        return;
      }

      const timestamp = conversation.end_time || conversation.start_time;
      const date = timestamp ? new Date(timestamp) : null;
      let label = 'Older';

      if (date && !Number.isNaN(date.getTime())) {
        const today = new Date(now);
        today.setHours(0, 0, 0, 0);

        const messageDay = new Date(date);
        messageDay.setHours(0, 0, 0, 0);

        const diffDays = Math.floor((today - messageDay) / 86400000);

        if (diffDays === 0) label = 'Today';
        else if (diffDays === 1) label = 'Yesterday';
        else if (diffDays < 7) label = 'This week';
        else if (diffDays < 30) label = 'This month';
      }

      if (!buckets[label]) {
        buckets[label] = [];
      }

      buckets[label].push(conversation);
    });

    // Most recently starred first, independent of conversation activity.
    if (buckets.Starred) {
      buckets.Starred.sort(
        (a, b) =>
          new Date(b.starred_at || 0).getTime() - new Date(a.starred_at || 0).getTime()
      );
    }

    return order.reduce((accumulator, key) => {
      if (buckets[key]?.length) {
        accumulator[key] = buckets[key];
      }
      return accumulator;
    }, {});
  }, [selectedConversations]);

  const assistantStats = useMemo(
    () => ({
      total: assistants.length,
      chat: assistants.filter((assistant) => assistant.supports_chat).length,
      voice: assistants.filter((assistant) => assistant.supports_realtime).length,
    }),
    [assistants]
  );

  const syncDraftSeeds = useCallback((assistantList) => {
    setDraftMessagesByAssistant((current) => {
      const next = { ...current };
      assistantList.forEach((assistant) => {
        if (!next[assistant.id]) {
          next[assistant.id] = buildSeedConversation(assistant.placeholder || assistant.greeting);
        }
      });
      return next;
    });
  }, []);

  const fetchConversationMessages = useCallback(async (assistantId, conversationExternalId) => {
    if (!assistantId || !conversationExternalId) {
      return;
    }

    setMessagesLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/business-assistants/${assistantId}/conversations/${conversationExternalId}/messages`,
        {
          headers: requestHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load conversation (${response.status})`);
      }

      const data = await response.json();
      setConversationMessages((current) => ({
        ...current,
        // Hidden messages were never shown to the person chatting, so they stay
        // out of the transcript here; staff can still read them in Logs.
        [conversationExternalId]: (data.messages || [])
          .filter((message) => !message.hidden)
          .map((message) => ({
            ...message,
            role: normalizeMessageRole(message.role),
          })),
      }));
    } catch (fetchError) {
      setError(fetchError.message);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  const fetchConversationList = useCallback(
    async (assistantId, options = {}) => {
      if (!assistantId) {
        return;
      }

      const {
        defaultToLatest = false,
        preferredConversationId,
        skipMessages = false,
        silent = false,
      } = options;

      if (!silent) {
        setHistoryLoading(true);
      }
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/business-assistants/${assistantId}/conversations`,
          { headers: requestHeaders() }
        );

        if (!response.ok) {
          throw new Error(`Failed to load history (${response.status})`);
        }

        const data = await response.json();
        const conversations = Array.isArray(data.conversations) ? data.conversations : [];

        setConversationsByAssistant((current) => ({
          ...current,
          [assistantId]: conversations,
        }));

        const shouldUpdateActive = preferredConversationId !== undefined || defaultToLatest;

        if (shouldUpdateActive) {
          let nextConversationId =
            preferredConversationId !== undefined
              ? preferredConversationId
              : activeConversationIdsRef.current[assistantId] || null;

          if (
            nextConversationId &&
            !conversations.some(
              (conversation) => conversation.external_id === nextConversationId
            )
          ) {
            nextConversationId = null;
          }

          if (!nextConversationId && defaultToLatest && conversations.length > 0) {
            nextConversationId = conversations[0].external_id;
          }

          setActiveConversationIds((current) => ({
            ...current,
            [assistantId]: nextConversationId,
          }));

          if (nextConversationId && !skipMessages) {
            await fetchConversationMessages(assistantId, nextConversationId);
          }
        }
      } catch (fetchError) {
        setError(fetchError.message);
      } finally {
        if (!silent) {
          setHistoryLoading(false);
        }
      }
    },
    [fetchConversationMessages]
  );

  const loadAssistants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/business-assistants`, {
        headers: requestHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to load business assistants (${response.status})`);
      }

      const data = await response.json();
      const list = Array.isArray(data.assistants) ? data.assistants : [];

      setAssistants(list);
      syncDraftSeeds(list);
    } catch (fetchError) {
      setError(fetchError.message);
    } finally {
      setLoading(false);
    }
  }, [syncDraftSeeds]);

  useEffect(() => {
    loadAssistants().catch(() => {});
  }, [loadAssistants]);

  useEffect(() => {
    if (!isChatPage || !selectedAssistant) {
      return;
    }

    fetchConversationList(selectedAssistant.id, { skipMessages: true }).catch(() => {});
  }, [fetchConversationList, isChatPage, selectedAssistant]);

  useEffect(() => {
    if (!loading && isChatPage && routeAssistantId && !selectedAssistant) {
      navigate('/businessassistants', { replace: true });
    }
  }, [loading, isChatPage, navigate, routeAssistantId, selectedAssistant]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !messagesEndRef.current) {
      return;
    }
    const nearBottomThreshold = 140;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom <= nearBottomThreshold) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [activeMessages, voiceTranscript, sending]);

  useEffect(() => {
    return () => {
      if (processorNodeRef.current) {
        processorNodeRef.current.disconnect();
        processorNodeRef.current.onaudioprocess = null;
      }
      if (mediaSourceRef.current) {
        mediaSourceRef.current.disconnect();
      }
      if (monitorGainRef.current) {
        monitorGainRef.current.disconnect();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (pendingMicStreamRef.current) {
        pendingMicStreamRef.current.getTracks().forEach((track) => track.stop());
        pendingMicStreamRef.current = null;
      }
      playbackSourcesRef.current.forEach((source) => {
        try {
          source.stop();
        } catch {
          // no-op
        }
      });
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.onmessage = null;
        try {
          wsRef.current.close();
        } catch {
          // no-op
        }
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  const ensureAudioContext = useCallback(async () => {
    if (!audioContextRef.current) {
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioContextCtor();
    }

    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }

    return audioContextRef.current;
  }, []);

  const resetVoiceTranscript = useCallback(() => {
    transcriptBuffersRef.current = {};
    setVoiceTranscript('');
  }, []);

  const stopPlayback = useCallback(() => {
    playbackSourcesRef.current.forEach((source) => {
      try {
        source.stop();
      } catch {
        // no-op
      }
    });
    playbackSourcesRef.current = [];
    playbackCursorRef.current = audioContextRef.current
      ? audioContextRef.current.currentTime
      : 0;
  }, []);

  const stopMicrophoneCapture = useCallback((commit = true) => {
    if (processorNodeRef.current) {
      processorNodeRef.current.disconnect();
      processorNodeRef.current.onaudioprocess = null;
      processorNodeRef.current = null;
    }
    if (mediaSourceRef.current) {
      mediaSourceRef.current.disconnect();
      mediaSourceRef.current = null;
    }
    if (monitorGainRef.current) {
      monitorGainRef.current.disconnect();
      monitorGainRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    const socket = wsRef.current;
    if (commit && socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'audio', audio: '', commit: true }));
    }

    setVoiceRecording(false);
  }, []);

  const finalizeVoiceSession = useCallback(() => {
    stopMicrophoneCapture(false);
    // A pre-flighted stream that never reached startMicrophoneCapture (session
    // failed or was cancelled mid-connect) must be released here.
    stopStream(pendingMicStreamRef.current);
    pendingMicStreamRef.current = null;
    stopPlayback();
    resetVoiceTranscript();
    // Unblock any pending greeting-gate Promise so handleStartVoice can exit
    // cleanly when the socket closes before the greeting arrives.
    if (partialGreetingTimerRef.current) {
      clearTimeout(partialGreetingTimerRef.current);
      partialGreetingTimerRef.current = null;
    }
    if (greetingResolverRef.current) {
      greetingResolverRef.current();
      greetingResolverRef.current = null;
    }
    audioArrivedRef.current = false;
    transcriptArrivedRef.current = false;
    wsRef.current = null;
    activeAssistantRef.current = null;
    voiceConversationIdRef.current = null;
    voiceAssistantIdRef.current = null;
    setVoiceConnected(false);
    setVoiceConnecting(false);
    setVoiceRecording(false);
    setVoiceStatus('Idle');
  }, [resetVoiceTranscript, stopMicrophoneCapture, stopPlayback]);

  const startMicrophoneCapture = async () => {
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return;
    }

    // The stream was already obtained by the pre-flight permission check in
    // handleStartVoice, so the native prompt appears on the user's tap rather
    // than seconds later once the socket and greeting have settled.
    const stream = pendingMicStreamRef.current;
    pendingMicStreamRef.current = null;
    if (!stream) {
      return;
    }

    let audioContext;
    try {
      audioContext = await ensureAudioContext();
    } catch (audioError) {
      // Never leak a live mic track (and its recording indicator) if the audio
      // graph cannot be built.
      stopStream(stream);
      throw audioError;
    }

    const mediaSource = audioContext.createMediaStreamSource(stream);
    const processorNode = audioContext.createScriptProcessor(4096, 1, 1);
    const monitorGain = audioContext.createGain();
    monitorGain.gain.value = 0;

    // Forward every audio chunk to the server without filtering.
    // OpenAI server VAD (threshold: 0.50, silence_duration_ms: 500) is
    // responsible for detecting end-of-turn.  Dropping silent frames
    // client-side breaks server VAD timing and prevents response generation.
    processorNode.onaudioprocess = (event) => {
      const liveSocket = wsRef.current;
      if (!liveSocket || liveSocket.readyState !== WebSocket.OPEN) {
        return;
      }

      const input = event.inputBuffer.getChannelData(0);
      const resampled = resampleFloat32(input, audioContext.sampleRate, OUTPUT_SAMPLE_RATE);
      const pcm16 = floatToPcm16(resampled);
      liveSocket.send(pcm16.buffer);
    };

    mediaSource.connect(processorNode);
    processorNode.connect(monitorGain);
    monitorGain.connect(audioContext.destination);

    mediaStreamRef.current = stream;
    mediaSourceRef.current = mediaSource;
    processorNodeRef.current = processorNode;
    monitorGainRef.current = monitorGain;
    setVoiceRecording(true);
    setVoiceStatus('Listening');
  };

  const upsertLiveTranscriptMessage = useCallback(
    ({ conversationId, role, itemId, delta, isFinal }) => {
      if (!conversationId || !delta) {
        return;
      }

      const normalizedRole = normalizeMessageRole(role);
      const liveKey = `live:${normalizedRole}:${itemId || LIVE_TRANSCRIPT_KEY}`;

      setConversationMessages((current) => {
        const existing = current[conversationId] || [];
        const index = existing.findIndex((message) => message.liveKey === liveKey);
        let nextMessages;

        if (index === -1) {
          nextMessages = [
            ...existing,
            {
              liveKey,
              role: normalizedRole,
              content: delta,
              timestamp: new Date().toISOString(),
              live: true,
              isFinal: !!isFinal,
            },
          ];
        } else {
          nextMessages = [...existing];
          nextMessages[index] = {
            ...nextMessages[index],
            content: `${nextMessages[index].content || ''}${delta}`,
            isFinal: !!isFinal,
          };
        }

        const withOrder = nextMessages.map((message, messageIndex) => ({
          ...message,
          _sortIndex:
            typeof message._sortIndex === 'number' ? message._sortIndex : messageIndex,
        }));

        withOrder.sort((a, b) => {
          const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;

          if (a.live && b.live) {
            const sameMoment = Math.abs(aTime - bTime) < 1500;

            if (sameMoment && a.role !== b.role) {
              if (a.role === 'user') return -1;
              if (b.role === 'user') return 1;
            }
          }

          if (aTime !== bTime) {
            return aTime - bTime;
          }

          return a._sortIndex - b._sortIndex;
        });

        return {
          ...current,
          [conversationId]: withOrder.map(({ _sortIndex, ...message }) => message),
        };
      });
    },
    []
  );

  const handleVoiceMessage = useCallback(
    async (event) => {
      if (typeof event.data !== 'string') {
        const buffer =
          event.data instanceof Blob ? await event.data.arrayBuffer() : event.data;

        if (!buffer || buffer.byteLength === 0) {
          return;
        }

        const audioContext = await ensureAudioContext();
        const float32 = pcm16ToFloat32(buffer);
        const audioBuffer = audioContext.createBuffer(1, float32.length, OUTPUT_SAMPLE_RATE);
        audioBuffer.copyToChannel(float32, 0);

        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContext.destination);

        const startTime = Math.max(
          playbackCursorRef.current,
          audioContext.currentTime + 0.02
        );
        source.start(startTime);
        playbackCursorRef.current = startTime + audioBuffer.duration;

        playbackSourcesRef.current.push(source);
        source.onended = () => {
          playbackSourcesRef.current = playbackSourcesRef.current.filter(
            (item) => item !== source
          );
        };
        // First assistant audio frame, mark audio as arrived and attempt to
        // release the greeting gate.  The gate only resolves when both audio
        // AND transcript have arrived, or when the partial-signal grace timer
        // fires (see tryReleaseGreetingGate).
        if (!audioArrivedRef.current) {
          audioArrivedRef.current = true;
          tryReleaseGreetingGate();
        }
        setVoiceStatus('Assistant speaking');
        return;
      }

      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }

      if (payload.type === 'session_started') {
        const liveConversationId = payload.conversation_id || null;
        const liveAssistantId = payload.agent_id || activeAssistantRef.current;
        const resumed = Boolean(payload.resumed);
        voiceConversationIdRef.current = liveConversationId;
        voiceAssistantIdRef.current = liveAssistantId;
        // Used by socket.onclose to decide whether to clear the active
        // conversation selection (we only clear for brand-new voice-only
        // conversations).
        voiceResumedRef.current = resumed;

        // If we're resuming an existing chat conversation, the backend will not
        // play the opening greeting. Unblock the greeting gate immediately so
        // the microphone can start without waiting for the 2.5s failsafe.
        if (resumed && greetingResolverRef.current) {
          greetingResolverRef.current();
          greetingResolverRef.current = null;
        }

        if (liveAssistantId && liveConversationId) {
          setActiveConversationIds((current) => ({
            ...current,
            [liveAssistantId]: liveConversationId,
          }));
          setConversationMessages((current) => ({
            ...current,
            [liveConversationId]: current[liveConversationId] || [],
          }));
          fetchConversationList(liveAssistantId, {
            preferredConversationId: liveConversationId,
            skipMessages: true,
            silent: true,
          }).catch(() => {});
        }

        setVoiceStatus('Connected');
        return;
      }

      if (payload.type === 'transcript_delta') {
        const role = payload.role || 'assistant';
        const itemId = payload.item_id || LIVE_TRANSCRIPT_KEY;
        const key = `${role}:${itemId}`;
        const delta = payload.delta || '';

        // First assistant transcript delta, mark transcript as arrived and
        // attempt to release the greeting gate.
        if (role !== 'user' && !transcriptArrivedRef.current) {
          transcriptArrivedRef.current = true;
          tryReleaseGreetingGate();
        }

        transcriptBuffersRef.current = {
          ...transcriptBuffersRef.current,
          [key]: `${transcriptBuffersRef.current[key] || ''}${delta}`,
        };
        setVoiceTranscript(transcriptBuffersRef.current[key] || '');

        upsertLiveTranscriptMessage({
          conversationId: voiceConversationIdRef.current,
          role,
          itemId,
          delta,
          isFinal: payload.is_final,
        });

        if (payload.is_final) {
          setTimeout(() => setVoiceTranscript(''), 1800);
        }

        setVoiceStatus(payload.is_final ? 'Transcript updated' : 'Streaming transcript');
        return;
      }

      if (payload.type === 'tool_start') {
        setVoiceStatus(`Running ${payload.tool_name}`);
        return;
      }

      if (payload.type === 'tool_end') {
        setVoiceStatus(`${payload.tool_name} finished`);
        return;
      }

      if (payload.type === 'audio_interrupted') {
        stopPlayback();
        setVoiceStatus('Assistant interrupted');
        return;
      }

      if (payload.type === 'error') {
        setError(payload.message || 'Realtime voice session failed.');
        setVoiceStatus('Voice error');
        if (payload.fatal && wsRef.current) {
          wsRef.current.close();
        }
      }
    },
    [
      ensureAudioContext,
      fetchConversationList,
      stopPlayback,
      upsertLiveTranscriptMessage,
    ]
  );

  const ensureVoiceSession = useCallback(async () => {
    if (!selectedAssistant) {
      return;
    }

    if (!selectedAssistant.supports_realtime) {
      throw new Error('Realtime voice is not enabled for this assistant.');
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('Missing access token.');
    }

    if (
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN &&
      activeAssistantRef.current === selectedAssistant.id
    ) {
      return;
    }

    if (wsRef.current) {
      wsRef.current.close();
    }
    await ensureAudioContext();

    resetVoiceTranscript();
    voiceConversationIdRef.current = null;
    voiceAssistantIdRef.current = selectedAssistant.id;
    activeAssistantRef.current = selectedAssistant.id;
    voiceResumedRef.current = false;

    setVoiceConnected(false);
    setVoiceStatus('Connecting');

    const resumeConversationId =
      activeConversationIdsRef.current[selectedAssistant.id] || null;

    const socket = new WebSocket(
      buildRealtimeWsUrl(selectedAssistant.realtime_ws_path, token, resumeConversationId)
    );
    socket.binaryType = 'arraybuffer';
    socket.onmessage = (messageEvent) => {
      handleVoiceMessage(messageEvent).catch((messageError) => {
        setError(messageError.message);
      });
    };

    socket.onclose = () => {
      const closedAssistantId = voiceAssistantIdRef.current;

      finalizeVoiceSession();

      if (closedAssistantId) {
        // Keep the activeConversationId selected even after voice disconnect so
        // the user can seamlessly continue the same conversation in chat.

        fetchConversationList(closedAssistantId, {
          // Refresh history so the completed voice conversation appears.
          skipMessages: true,
          silent: true,
        }).catch(() => {});
      }
    };

    const openPromise = new Promise((resolve, reject) => {
      let settled = false;

      const handleOpen = () => {
        if (settled) {
          return;
        }
        settled = true;
        socket.removeEventListener('open', handleOpen);
        socket.removeEventListener('error', handleError);
        resolve();
      };

      const handleError = () => {
        if (settled) {
          return;
        }
        settled = true;
        socket.removeEventListener('open', handleOpen);
        socket.removeEventListener('error', handleError);
        reject(new Error('Failed to connect realtime voice websocket.'));
      };

      socket.addEventListener('open', handleOpen);
      socket.addEventListener('error', handleError);
    });

    wsRef.current = socket;
    await openPromise;
    setVoiceConnected(true);
    setVoiceStatus('Connected');
  }, [
    ensureAudioContext,
    fetchConversationList,
    finalizeVoiceSession,
    handleVoiceMessage,
    resetVoiceTranscript,
    selectedAssistant,
  ]);

  const handleStartVoice = async () => {
    try {
      setError(null);
      setMicBlock(null);

      // Ask for the microphone FIRST, while we are still inside the task
      // started by the user's tap. Calling getUserMedia later, after the
      // websocket handshake and the greeting gate, detaches the request from
      // the gesture, which mobile Safari in particular can suppress, and it
      // means a previously-blocked mic surfaces as a raw NotAllowedError
      // instead of actionable guidance.
      const permission = await requestMicrophoneAccess();
      if (permission.state !== MIC_PERMISSION_STATE.GRANTED) {
        setMicBlock({ state: permission.state, message: permission.message });
        return;
      }
      pendingMicStreamRef.current = permission.stream;

      setVoiceConnecting(true);
      // Reset greeting gate for this new session attempt.
      audioArrivedRef.current = false;
      transcriptArrivedRef.current = false;
      if (partialGreetingTimerRef.current) {
        clearTimeout(partialGreetingTimerRef.current);
        partialGreetingTimerRef.current = null;
      }
      greetingResolverRef.current = null;

      await ensureVoiceSession();

      // Wait for the first assistant audio or transcript before opening the
      // mic.  This prevents the user's own microphone audio from being captured
      // and sent to OpenAI before the greeting has been spoken, which can
      // confuse server VAD and cause the agent to start responding before it
      // has finished its opening line.
      //
      // Failsafe: if no greeting signal arrives within 2.5 s (e.g. network
      // latency or a non-greeting configuration), start the mic anyway so the
      // user is never permanently stuck waiting.
      setVoiceStatus('Assistant greeting…');
      await new Promise((resolve) => {
        // If the socket already closed while we were connecting, unblock
        // immediately, finalizeVoiceSession() will have set wsRef.current to
        // null and resolved any pending greetingResolverRef.
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          resolve();
          return;
        }
        const timeoutId = setTimeout(() => {
          greetingResolverRef.current = null;
          resolve();
        }, 2500);
        greetingResolverRef.current = () => {
          clearTimeout(timeoutId);
          greetingResolverRef.current = null;
          resolve();
        };

        // If session_started already arrived and told us this is a resumed
        // conversation (no greeting will play), unblock immediately.
        if (voiceResumedRef.current && greetingResolverRef.current) {
          greetingResolverRef.current();
        }
      });

      await startMicrophoneCapture();
    } catch (voiceError) {
      setError(voiceError.message);
      if (wsRef.current) {
        wsRef.current.close();
      } else {
        finalizeVoiceSession();
      }
    } finally {
      setVoiceConnecting(false);
    }
  };

  // tryReleaseGreetingGate, resolves the greeting gate Promise only when both
  // the first assistant audio frame AND the first assistant transcript delta have
  // arrived.  If only one of the two signals arrives, a 2 500 ms grace timer is
  // started; if the second signal does not arrive in time, the gate is released
  // anyway so the user is never permanently blocked.
  //
  // This function intentionally references refs directly (not closures over
  // state) so it does not need to be wrapped in useCallback, it is a plain
  // function defined after all the refs it reads.
  const tryReleaseGreetingGate = () => {
    const bothArrived = audioArrivedRef.current && transcriptArrivedRef.current;

    if (bothArrived) {
      // Both signals received, resolve immediately and cancel any grace timer.
      if (partialGreetingTimerRef.current) {
        clearTimeout(partialGreetingTimerRef.current);
        partialGreetingTimerRef.current = null;
      }
      if (greetingResolverRef.current) {
        greetingResolverRef.current();
        greetingResolverRef.current = null;
      }
      return;
    }

    // Only one signal so far, start the grace timer once (2 500 ms) so the
    // user is not permanently stuck when only audio or only transcript arrives
    // (e.g. a text-only voice path or a slow audio pipeline).
    if (!partialGreetingTimerRef.current && greetingResolverRef.current) {
      partialGreetingTimerRef.current = setTimeout(() => {
        partialGreetingTimerRef.current = null;
        if (greetingResolverRef.current) {
          greetingResolverRef.current();
          greetingResolverRef.current = null;
        }
      }, 2500);
    }
  };

  const handleStopRecording = () => {
    stopMicrophoneCapture(true);
    setVoiceStatus('Waiting for assistant');
  };

  const handleDisconnectVoice = () => {
    if (wsRef.current && wsRef.current.readyState < WebSocket.CLOSING) {
      wsRef.current.close();
      return;
    }
    finalizeVoiceSession();
  };

  const handleOpenAssistant = useCallback(
    (assistantId) => {
      const hasLiveVoice =
        wsRef.current && wsRef.current.readyState < WebSocket.CLOSING;

      if (hasLiveVoice) {
        const ok = window.confirm(
          'A voice session is in progress. Open a new workspace and end the call?'
        );
        if (!ok) return;
        wsRef.current.close();
      } else {
        finalizeVoiceSession();
      }

      setActiveConversationIds((current) => ({ ...current, [assistantId]: null }));
      setDraftMessagesByAssistant((current) => {
        const { [assistantId]: _removed, ...rest } = current;
        return rest;
      });
      setDraft('');
      setError(null);
      navigate(`/businessassistants/${assistantId}`);
    },
    [finalizeVoiceSession, navigate]
  );
  const handleExitWorkspace = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState < WebSocket.CLOSING) {
      wsRef.current.close();
    } else {
      finalizeVoiceSession();
    }

    setDraft('');
    setError(null);
    navigate('/businessassistants');
  }, [finalizeVoiceSession, navigate]);

  const handleStartNewConversation = () => {
    if (!selectedAssistant) {
      return;
    }

    // Disconnect any active voice session before switching to a new conversation.
    if (wsRef.current && wsRef.current.readyState < WebSocket.CLOSING) {
      wsRef.current.close();
    } else if (voiceConnected || voiceConnecting) {
      finalizeVoiceSession();
    }

    setActiveConversationIds((current) => ({
      ...current,
      [selectedAssistant.id]: null,
    }));
    setDraftMessagesByAssistant((current) => ({
      ...current,
      [selectedAssistant.id]: buildSeedConversation(selectedAssistant.placeholder || selectedAssistant.greeting),
    }));
    setDraft('');
    setError(null);
    // On mobile the sidebar is an overlay drawer, leaving it open hides the
    // fresh chat the user just asked for.
    setSidebarOpen(false);
  };

  const handleSelectConversation = async (conversationExternalId) => {
    if (!selectedAssistant) {
      return;
    }

    // Disconnect any active voice session before loading a past conversation.
    if (wsRef.current && wsRef.current.readyState < WebSocket.CLOSING) {
      wsRef.current.close();
    } else if (voiceConnected || voiceConnecting) {
      finalizeVoiceSession();
    }

    setActiveConversationIds((current) => ({
      ...current,
      [selectedAssistant.id]: conversationExternalId,
    }));
    setSidebarOpen(false);
    await fetchConversationMessages(selectedAssistant.id, conversationExternalId);
  };

  // ── Conversation management: rename / star / soft delete ──────────────────
  // All three patch the local list optimistically and roll back on failure, so
  // the sidebar stays responsive without a full refetch.

  // Dismiss an open row menu on any outside click or Escape.
  useEffect(() => {
    if (!openConversationMenuId) {
      return undefined;
    }

    const closeMenu = () => {
      setOpenConversationMenuId(null);
      setPendingDeleteConversationId(null);
      setConversationMenuPos(null);
    };
    const handlePointerDown = (event) => {
      if (event.target.closest('.gptConvMenu, [data-conv-menu-trigger]')) {
        return;
      }
      closeMenu();
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    // The menu is positioned in viewport coordinates, so any scroll would leave
    // it floating away from its row.
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [openConversationMenuId]);

  const patchConversationLocally = useCallback((assistantId, externalId, changes) => {
    setConversationsByAssistant((current) => ({
      ...current,
      [assistantId]: (current[assistantId] || []).map((conversation) =>
        conversation.external_id === externalId
          ? { ...conversation, ...changes }
          : conversation
      ),
    }));
  }, []);

  const handleRenameConversation = useCallback(
    async (conversationExternalId, nextTitle) => {
      if (!selectedAssistant) {
        return;
      }

      const assistantId = selectedAssistant.id;
      const trimmed = (nextTitle || '').trim().slice(0, 255);
      const previous = (conversationsByAssistant[assistantId] || []).find(
        (conversation) => conversation.external_id === conversationExternalId
      );

      setRenamingConversationId(null);
      // A blank title clears the custom name and restores the auto preview.
      patchConversationLocally(assistantId, conversationExternalId, {
        title: trimmed || null,
      });

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/business-assistants/${assistantId}/conversations/${conversationExternalId}`,
          {
            method: 'PATCH',
            headers: requestHeaders(),
            body: JSON.stringify({ title: trimmed }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to rename conversation (${response.status})`);
        }
      } catch (renameError) {
        patchConversationLocally(assistantId, conversationExternalId, {
          title: previous ? previous.title ?? null : null,
        });
        setError(renameError.message);
      }
    },
    [conversationsByAssistant, patchConversationLocally, selectedAssistant]
  );

  const handleToggleStarConversation = useCallback(
    async (conversationExternalId, nextStarred) => {
      if (!selectedAssistant) {
        return;
      }

      const assistantId = selectedAssistant.id;
      patchConversationLocally(assistantId, conversationExternalId, {
        is_starred: nextStarred,
        starred_at: nextStarred ? new Date().toISOString() : null,
      });

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/business-assistants/${assistantId}/conversations/${conversationExternalId}/star`,
          {
            method: 'POST',
            headers: requestHeaders(),
            body: JSON.stringify({ starred: nextStarred }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to update star (${response.status})`);
        }
      } catch (starError) {
        patchConversationLocally(assistantId, conversationExternalId, {
          is_starred: !nextStarred,
          starred_at: nextStarred ? null : new Date().toISOString(),
        });
        setError(starError.message);
      }
    },
    [patchConversationLocally, selectedAssistant]
  );

  const handleDeleteConversation = useCallback(
    async (conversationExternalId) => {
      if (!selectedAssistant) {
        return;
      }

      const assistantId = selectedAssistant.id;
      const removed = (conversationsByAssistant[assistantId] || []).find(
        (conversation) => conversation.external_id === conversationExternalId
      );

      setPendingDeleteConversationId(null);

      // Drop it from the sidebar immediately, the row itself is only
      // soft-deleted server-side and stays available in the conversation logs.
      setConversationsByAssistant((current) => ({
        ...current,
        [assistantId]: (current[assistantId] || []).filter(
          (conversation) => conversation.external_id !== conversationExternalId
        ),
      }));

      // If the open conversation was deleted, fall back to a fresh chat.
      if (activeConversationIdsRef.current[assistantId] === conversationExternalId) {
        setActiveConversationIds((current) => ({ ...current, [assistantId]: null }));
        setDraftMessagesByAssistant((current) => ({
          ...current,
          [assistantId]: buildSeedConversation(
            selectedAssistant.placeholder || selectedAssistant.greeting
          ),
        }));
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/business-assistants/${assistantId}/conversations/${conversationExternalId}`,
          {
            method: 'DELETE',
            headers: requestHeaders(),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to delete conversation (${response.status})`);
        }
      } catch (deleteError) {
        if (removed) {
          setConversationsByAssistant((current) => ({
            ...current,
            [assistantId]: [...(current[assistantId] || []), removed],
          }));
        }
        setError(deleteError.message);
      }
    },
    [conversationsByAssistant, selectedAssistant]
  );
  const closeVoiceSocketAndWait = useCallback(() => {
    return new Promise((resolve) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState === WebSocket.CLOSED) {
        finalizeVoiceSession();
        resolve();
        return;
      }
      // Chain onto existing onclose so finalizeVoiceSession still runs.
      const prevOnClose = ws.onclose;
      ws.onclose = (e) => {
        if (prevOnClose) prevOnClose.call(ws, e);
        resolve();
      };
      if (ws.readyState < WebSocket.CLOSING) ws.close();
    });
  }, [finalizeVoiceSession]);

  const MAX_ATTACH_BYTES = 50 * 1024 * 1024;

  // Read a File into a base64 string (without the "data:<type>;base64," prefix).
  // The backend decodes this back to raw bytes before sending to the LLM.
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result || '';
        const commaIndex = result.indexOf(',');
        resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const handleFileSelect = (event) => {
    const incoming = Array.from(event.target.files || []);
    if (!incoming.length) return;
    if (!supportsVision) {
      const images = incoming.filter((file) => isImageMediaType(file.type));
      if (images.length) {
        setError(VISION_UNSUPPORTED_MESSAGE);
        event.target.value = '';
        return;
      }
    }

    const combined = [...attachedFiles, ...incoming];
    const totalSize = combined.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > MAX_ATTACH_BYTES) {
      setError('Total attachment size cannot exceed 50 MB.');
      event.target.value = '';
      return;
    }
    setAttachedFiles(combined);
    event.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async (event) => {
    event.preventDefault();
    if (
      !selectedAssistant ||
      !selectedAssistant.supports_chat ||
      (!draft.trim() && attachedFiles.length === 0) ||
      sending
    ) {
      return;
    }

    const shouldEndVoiceSession =
      voiceConnected ||
      voiceConnecting ||
      (wsRef.current && wsRef.current.readyState < WebSocket.CLOSING);


    if (shouldEndVoiceSession) {
      await closeVoiceSocketAndWait();
    }

    const message = draft.trim();
    const assistantId = selectedAssistant.id;

    const conversationIdToSend = activeConversationIdsRef.current[assistantId] || null;

    const existingMessages = conversationIdToSend
      ? conversationMessages[conversationIdToSend] || []
      : (draftMessagesByAssistant[assistantId] || []).filter((m) => !m.seed);

    const filesToSend = [...attachedFiles];

    // Build attachment previews so the user's message renders the document /
    // image inline (ChatGPT-style). Image previews use an object URL; other
    // file types fall back to a labelled file card.
    const attachmentPreviews = filesToSend.map((file) => ({
      name: file.name,
      media_type: file.type || 'application/octet-stream',
      size: file.size,
      url: isImageMediaType(file.type) ? URL.createObjectURL(file) : null,
    }));

    const optimisticMessages = [
      ...existingMessages,
      {
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
        local: true,
        attachments: attachmentPreviews,
      },
    ];
    setError(null);
    setDraft('');
    setAttachedFiles([]);
    setSending(true);

    if (conversationIdToSend) {
      setConversationMessages((current) => ({
        ...current,
        [conversationIdToSend]: optimisticMessages,
      }));
    } else {
      setDraftMessagesByAssistant((current) => ({
        ...current,
        [assistantId]: optimisticMessages,
      }));
    }

    try {
      const encodedFiles = await Promise.all(
        filesToSend.map(async (file) => ({
          filename: file.name,
          media_type: file.type || 'application/octet-stream',
          data: await fileToBase64(file),
        }))
      );

      const response = await fetch(
        `${API_BASE_URL}/api/business-assistants/${assistantId}/chat`,
        {
          method: 'POST',
          headers: requestHeaders(),
          body: JSON.stringify({
            message,
            conversation_id: conversationIdToSend,
            files: encodedFiles,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Chat request failed (${response.status})`);
      }

      const data = await response.json();
      const resolvedConversationId = data.conversation_id;
      // Render tool/knowledge calls inline (before the assistant reply) so the
      // user sees them right away instead of only after reloading history.
      const toolCallMessages = (data.tool_calls || []).map((call, callIndex) => ({
        role: normalizeMessageRole(call.role),
        tool_data: call.tool_data || {},
        content: call.tool_data?.name || '',
        timestamp: new Date().toISOString(),
        local: true,
        liveKey: `local-tool:${resolvedConversationId}:${callIndex}`,
      }));
      const finalMessages = [
        ...optimisticMessages,
        ...toolCallMessages,
        {
          role: 'assistant',
          content: data.response || '',
          timestamp: new Date().toISOString(),
          local: true,
        },
      ];

      setConversationMessages((current) => ({
        ...current,
        [resolvedConversationId]: finalMessages,
      }));
      setActiveConversationIds((current) => ({
        ...current,
        [assistantId]: resolvedConversationId,
      }));
      setDraftMessagesByAssistant((current) => ({
        ...current,
        [assistantId]: buildSeedConversation(selectedAssistant.placeholder || selectedAssistant.greeting),
      }));

      await fetchConversationList(assistantId, {
        preferredConversationId: resolvedConversationId,
        skipMessages: true,
        silent: true,
      });
    } catch (chatError) {
      setError(chatError.message);
      setDraft(message);
      setAttachedFiles(filesToSend);
      if (conversationIdToSend) {
        setConversationMessages((current) => ({
          ...current,
          [conversationIdToSend]: existingMessages,
        }));
      } else {
        setDraftMessagesByAssistant((current) => ({
          ...current,
          [assistantId]: existingMessages,
        }));
      }
    } finally {
      setSending(false);
    }
  };

  // Derived voice panel state
  const voiceIsActive = voiceConnected || voiceConnecting;
  const micIsBlocked = micPermissionState === 'denied';

  // Only a blocked microphone needs the settings walkthrough. Missing hardware,
  // an insecure origin, or a device already in use are different problems and
  // get a plain explanation plus retry.
  const micRecovery = useMemo(
    () =>
      micBlock?.state === MIC_PERMISSION_STATE.DENIED ? buildMicRecoverySteps() : null,
    [micBlock]
  );
  const voicePanelModifier = voiceConnecting
    ? 'connecting'
    : voiceRecording
    ? 'recording'
    : voiceConnected
    ? ''
    : '';

  const voicePanelStatusLabel = voiceConnecting
    ? 'Connecting to voice…'
    : voiceRecording
    ? 'Listening…'
    : voiceStatus === 'Assistant speaking'
    ? 'Assistant speaking'
    : voiceStatus === 'Waiting for assistant'
    ? 'Processing…'
    : 'Voice connected';

  if (isChatPage) {
    if (loading) {
      return (
        <div className="baChatPage">
          <div className="baChatStatusCard">
            <h2>Loading assistant</h2>
            <p>Preparing the workspace and conversation history.</p>
          </div>
        </div>
      );
    }

    if (!selectedAssistant) {
      return (
        <div className="baChatPage">
          <div className="baChatStatusCard">
            <h2>Assistant not found</h2>
            <p>The business assistant you requested is not available.</p>
            <button
              type="button"
              className="baDirectoryOpenBtn"
              onClick={handleExitWorkspace}
            >
              Back to assistants
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="baChatPage">
        {/* Mobile sidebar backdrop */}
        <div
          className={`gptSidebarBackdrop${sidebarOpen ? ' visible' : ''}`}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />

        {/* ── Microphone permission recovery sheet ──
            Browsers expose no API to open their own permission settings, so the
            only workable recovery for a blocked mic is guided instructions plus
            a retry. Rendered as a bottom sheet on mobile, a centred dialog on
            desktop. */}
        {micBlock &&
          createPortal(
            <div
              className="micSheetOverlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="micSheetTitle"
              onClick={(event) => {
                if (event.target === event.currentTarget) {
                  setMicBlock(null);
                }
              }}
            >
              <div className="micSheet">
                <div className="micSheetHandle" aria-hidden="true" />

                <button
                  type="button"
                  className="micSheetClose"
                  onClick={() => setMicBlock(null)}
                  aria-label="Close"
                >
                  <X size={18} />
                </button>

                <div className="micSheetIcon">
                  <MicOff size={22} />
                </div>

                <h2 className="micSheetTitle" id="micSheetTitle">
                  {micRecovery ? 'Microphone access is off' : 'Voice can’t start'}
                </h2>
                <p className="micSheetLead">
                  {micRecovery
                    ? 'To talk to your assistant, this site needs permission to use your microphone. It takes a few seconds to turn on.'
                    : micBlock.message}
                </p>

                {micRecovery && (
                  <div className="micSheetSteps">
                    <p className="micSheetStepsTitle">
                      <Settings size={14} />
                      {micRecovery.title}
                    </p>
                    <ol>
                      {micRecovery.steps.map((step, stepIndex) => (
                        <li key={stepIndex}>{step}</li>
                      ))}
                    </ol>
                    {micRecovery.note && (
                      <p className="micSheetNote">{micRecovery.note}</p>
                    )}
                  </div>
                )}

                <div className="micSheetActions">
                  <button
                    type="button"
                    className="micSheetPrimary"
                    onClick={() => {
                      setMicBlock(null);
                      handleStartVoice();
                    }}
                  >
                    <Mic size={15} />
                    Try again
                  </button>
                  <button
                    type="button"
                    className="micSheetSecondary"
                    onClick={() => setMicBlock(null)}
                  >
                    Continue with text
                  </button>
                </div>

                <p className="micSheetFootnote">
                  You can keep chatting by typing, voice is optional.
                </p>
              </div>
            </div>,
            document.body
          )}

        <div className="gptRoot">
          <aside className={`gptSidebar${sidebarOpen ? ' drawer-open' : ''}`}>
            <div className="gptSidebarTop">
              <p className="gptSidebarKicker">Business assistant</p>
              <button
                type="button"
                className="gptBackBtn"
                onClick={handleExitWorkspace}
              >
                <ArrowLeft size={15} />
                Back to list
              </button>
              <button
                type="button"
                className="gptNewChatBtn"
                onClick={handleStartNewConversation}
              >
                <Plus size={15} />
                New chat
              </button>
            </div>

            <div className="gptSidebarBody">
              {historyLoading ? (
                <p className="gptConvLoading">Loading conversations…</p>
              ) : Object.keys(groupedConversations).length === 0 ? (
                <p className="gptConvLoading">No conversations yet.</p>
              ) : (
                <div className="gptConvList">
                  {Object.entries(groupedConversations).map(([label, conversations]) => (
                    <div key={label} className="gptConvSection">
                      <p className="gptConvSectionLabel">{label}</p>
                      {conversations.map((conversation) => {
                        const conversationTime = formatHistoryTime(
                          conversation.end_time || conversation.start_time
                        );
                        const externalId = conversation.external_id;
                        const isRenaming = renamingConversationId === externalId;
                        const isMenuOpen = openConversationMenuId === externalId;
                        const isPendingDelete =
                          pendingDeleteConversationId === externalId;

                        // Inline rename replaces the row with a text input,
                        // committed on Enter/blur and abandoned on Escape.
                        if (isRenaming) {
                          return (
                            <div
                              key={externalId}
                              className="gptConvItem gptConvItemRenaming"
                            >
                              <MessageSquare size={13} />
                              <input
                                type="text"
                                className="gptConvRenameInput"
                                value={renameDraft}
                                maxLength={255}
                                autoFocus
                                placeholder="Conversation name"
                                onChange={(event) => setRenameDraft(event.target.value)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    handleRenameConversation(externalId, renameDraft);
                                  } else if (event.key === 'Escape') {
                                    event.preventDefault();
                                    setRenamingConversationId(null);
                                  }
                                }}
                                onBlur={() =>
                                  handleRenameConversation(externalId, renameDraft)
                                }
                              />
                            </div>
                          );
                        }

                        return (
                          <div
                            key={externalId}
                            className={`gptConvItem ${
                              activeConversationId === externalId ? 'active' : ''
                            }`}
                            role="button"
                            tabIndex={0}
                            onClick={(event) => {
                              if (event.target.closest('.gptConvItemActions')) {
                                return;
                              }
                              handleSelectConversation(externalId);
                            }}
                            onKeyDown={(event) => {
                              if (
                                (event.key === 'Enter' || event.key === ' ') &&
                                event.target === event.currentTarget
                              ) {
                                event.preventDefault();
                                handleSelectConversation(externalId);
                              }
                            }}
                          >
                            {conversation.is_starred ? (
                              <Star size={13} className="gptConvStarIcon" />
                            ) : (
                              <MessageSquare size={13} />
                            )}
                            <div className="gptConvItemText">
                              <span className="gptConvItemTitle">
                                {buildConversationTitle(conversation)}
                              </span>
                              {conversationTime && (
                                <span className="gptConvItemMeta">{conversationTime}</span>
                              )}
                            </div>

                            <div
                              className={`gptConvItemActions${
                                conversation.is_starred || isMenuOpen
                                  ? ' hasStarred'
                                  : ''
                              }`}
                            >
                              <button
                                type="button"
                                className={`gptConvIconBtn${
                                  conversation.is_starred ? ' starred' : ''
                                }`}
                                title={
                                  conversation.is_starred
                                    ? 'Remove star'
                                    : 'Star conversation'
                                }
                                aria-label={
                                  conversation.is_starred
                                    ? 'Remove star'
                                    : 'Star conversation'
                                }
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleToggleStarConversation(
                                    externalId,
                                    !conversation.is_starred
                                  );
                                }}
                              >
                                <Star size={13} />
                              </button>
                              <button
                                type="button"
                                className="gptConvIconBtn"
                                title="Conversation options"
                                aria-label="Conversation options"
                                aria-haspopup="menu"
                                aria-expanded={isMenuOpen}
                                data-conv-menu-trigger=""
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (!isMenuOpen) {
                                    setConversationMenuPos(
                                      buildConvMenuPosition(event.currentTarget)
                                    );
                                  }
                                  setPendingDeleteConversationId(null);
                                  setOpenConversationMenuId(
                                    isMenuOpen ? null : externalId
                                  );
                                }}
                              >
                                <MoreHorizontal size={13} />
                              </button>
                            </div>

                            {isMenuOpen && conversationMenuPos &&
                              createPortal(
                              <div
                                className="gptConvMenu"
                                role="menu"
                                style={conversationMenuPos}
                                onMouseDown={(event) => event.stopPropagation()}
                              >
                                {isPendingDelete ? (
                                  <>
                                    <p className="gptConvMenuNote">
                                      Remove from this assistant? It stays in your
                                      conversation logs.
                                    </p>
                                    <button
                                      type="button"
                                      className="gptConvMenuItem danger"
                                      role="menuitem"
                                      onClick={() => {
                                        setOpenConversationMenuId(null);
                                        handleDeleteConversation(externalId);
                                      }}
                                    >
                                      <Trash2 size={13} />
                                      Confirm delete
                                    </button>
                                    <button
                                      type="button"
                                      className="gptConvMenuItem"
                                      role="menuitem"
                                      onClick={() =>
                                        setPendingDeleteConversationId(null)
                                      }
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      className="gptConvMenuItem"
                                      role="menuitem"
                                      onClick={() => {
                                        setRenameDraft(conversation.title || '');
                                        setRenamingConversationId(externalId);
                                        setOpenConversationMenuId(null);
                                      }}
                                    >
                                      <Edit2 size={13} />
                                      Rename
                                    </button>
                                    <button
                                      type="button"
                                      className="gptConvMenuItem"
                                      role="menuitem"
                                      onClick={() => {
                                        setOpenConversationMenuId(null);
                                        handleToggleStarConversation(
                                          externalId,
                                          !conversation.is_starred
                                        );
                                      }}
                                    >
                                      <Star size={13} />
                                      {conversation.is_starred ? 'Unstar' : 'Star'}
                                    </button>
                                    <button
                                      type="button"
                                      className="gptConvMenuItem danger"
                                      role="menuitem"
                                      onClick={() =>
                                        setPendingDeleteConversationId(externalId)
                                      }
                                    >
                                      <Trash2 size={13} />
                                      Delete
                                    </button>
                                  </>
                                )}
                              </div>,
                              document.body
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="gptSidebarFooter">
              <div className="gptAssistantBadge">
                <span className="gptAssistantBadgeIcon">
                  <Briefcase size={13} />
                </span>
                <div className="gptAssistantBadgeText">
                  <span className="gptAssistantBadgeName">{selectedAssistant.name}</span>
                  <span className="gptAssistantBadgeMeta">
                    {userEmail || 'Organization assistant'}
                  </span>
                </div>
              </div>

              <div className="gptAssistantMeta">
                {selectedAssistant.supports_chat && <span className="baPill">Chat</span>}
                {selectedAssistant.supports_realtime && (
                  <span className="baPill">Voice</span>
                )}
              </div>
            </div>
          </aside>

          {/* ── Main pane, voiceActive class dims input zone ── */}
          <div className={`gptMain${voiceIsActive ? ' voiceActive' : ''}`}>
            <div className="gptTopBar">
              {/* Mobile hamburger, only visible on small screens via CSS */}
              <button
                type="button"
                className="gptSidebarToggle"
                onClick={() => setSidebarOpen((prev) => !prev)}
                title="Open conversation history"
                aria-label="Toggle sidebar"
              >
                {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
              </button>

              <div className="gptTopBarCopy">
                <span className="gptTopBarName">{selectedAssistant.name}</span>
                <p className="gptTopBarSubtitle">
                  {selectedAssistant.greeting ||
                    selectedAssistant.preview ||
                    'Internal business assistant workspace'}
                </p>
              </div>

              {/* The LLM model id and TTS voice name are implementation detail
                  with no action attached for a business user, so they are not
                  surfaced here. */}
              <div className="gptTopBarRight">
                {selectedAssistant.supports_realtime && (
                  <div
                    className={`gptVoiceBadge${voiceConnected ? ' live' : ''}`}
                    aria-live="polite"
                    role="status"
                  >
                    {voiceConnecting ? (
                      <span className="gptSpinner" />
                    ) : (
                      <Mic size={12} />
                    )}
                    {voiceConnecting ? 'Connecting…' : voiceStatus}
                  </div>
                )}
                {/* Mic toggle button */}
                <button
                  type="button"
                  className={`gptVoiceBtn${voiceRecording ? ' recording' : ''}${voiceConnecting ? ' connecting' : ''}${micIsBlocked && !voiceRecording ? ' micBlocked' : ''}`}
                  onClick={voiceRecording ? handleStopRecording : handleStartVoice}
                  disabled={!selectedAssistant.supports_realtime || voiceConnecting}
                  title={
                    voiceRecording
                      ? 'Stop recording'
                      : micIsBlocked
                      ? 'Microphone blocked, tap for help enabling it'
                      : 'Start voice'
                  }
                >
                  {voiceRecording ? (
                    <span className="gptMicWave" aria-hidden="true">
                      <span /><span /><span />
                    </span>
                  ) : micIsBlocked ? (
                    <MicOff size={16} />
                  ) : (
                    <Mic size={16} />
                  )}
                </button>
                {voiceConnected && (
                  <button
                    type="button"
                    className="gptVoiceBtn"
                    onClick={handleDisconnectVoice}
                    title="Disconnect voice"
                  >
                    <PhoneOff size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* ── Voice mode panel, slides in when voice is active ── */}
            {voiceIsActive && (
              <div
                className={`gptVoicePanel${voicePanelModifier ? ` ${voicePanelModifier}` : ''}`}
                role="status"
                aria-live="polite"
              >
                <div className="gptVoicePanelIcon">
                  {voiceConnecting ? (
                    <span className="gptSpinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                  ) : voiceRecording ? (
                    <span className="gptMicWave" aria-hidden="true">
                      <span /><span /><span />
                    </span>
                  ) : (
                    <Mic size={16} />
                  )}
                </div>
                <div className="gptVoicePanelBody">
                  <p className="gptVoicePanelStatus">{voicePanelStatusLabel}</p>
                  {voiceTranscript && (
                    <p className="gptVoicePanelTranscript">{voiceTranscript}</p>
                  )}
                </div>
              </div>
            )}

            <div className="gptMessages" ref={messagesContainerRef}>
              <div className="gptMessagesInner">
                {error && <div className="gptErrorBanner">{error}</div>}

                {messagesLoading ? (
                  <p className="gptMessagesLoading">Loading messages…</p>
                ) : activeMessages.length === 0 ? (
                  voiceIsActive ? (
                    // During an active voice session with no transcript yet,
                    // show a voice-specific empty state so the web-chat
                    // placeholder/greeting is not displayed as if it were a
                    // chat message, this was the "initial greeting of
                    // webchannel" UI bug.
                    <div className="gptEmptyState">
                      <div className="gptEmptyIcon">
                        <Mic size={26} />
                      </div>
                      <h2>{selectedAssistant.name}</h2>
                      <p>
                        {voiceConnecting
                          ? 'Connecting to voice…'
                          : 'Speak when you\'re ready, your conversation will appear here.'}
                      </p>
                    </div>
                  ) : (
                    <div className="gptEmptyState">
                      <div className="gptEmptyIcon">
                        <Briefcase size={26} />
                      </div>
                      <h2>{selectedAssistant.name}</h2>
                      <p>{selectedAssistant.placeholder || selectedAssistant.greeting || 'How can I help you today?'}</p>
                    </div>
                  )
                ) : (
                  <>
                    {displayedMessages.map((message, index) => {
                      const messageRole = normalizeMessageRole(message.role);
                      const messageKey = `${message.id || message.liveKey || message.timestamp || message.content}-${index}`;

                      // Tool / knowledge calls render as a compact collapsible
                      // row, not a chat bubble (matches the Logs view).
                      if (
                        messageRole === 'tool_call' ||
                        messageRole === 'knowledge_call'
                      ) {
                        return (
                          <GptToolCallRow
                            key={messageKey}
                            message={message}
                            role={messageRole}
                          />
                        );
                      }

                      return (
                        <div key={messageKey} className={`gptMsg ${messageRole}`}>
                          {messageRole === 'assistant' && (
                            <div className="gptMsgAvatar">
                              <Briefcase size={13} />
                            </div>
                          )}

                          <div className={`gptMsgContent${message.live && !message.isFinal ? ' streaming' : ''}`}>
                            {Array.isArray(message.attachments) &&
                              message.attachments.length > 0 && (
                                <div className="gptMsgAttachments">
                                  {message.attachments.map((attachment, attachmentIndex) => {
                                    // Optimistic (just-sent) attachments carry `name`
                                    // + an object-URL `url`; persisted ones from the
                                    // API carry `filename` and no url.
                                    const attachmentName =
                                      attachment.name || attachment.filename;
                                    return attachment.url ? (
                                      <a
                                        key={attachmentIndex}
                                        href={attachment.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="gptMsgImageAttachment"
                                      >
                                        <img src={attachment.url} alt={attachmentName} />
                                      </a>
                                    ) : (
                                      <div
                                        key={attachmentIndex}
                                        className="gptMsgFileAttachment"
                                      >
                                        <span className="gptMsgFileIcon">
                                          <FileText size={16} />
                                        </span>
                                        <div className="gptMsgFileMeta">
                                          <span className="gptMsgFileName">
                                            {attachmentName}
                                          </span>
                                          {formatFileSize(attachment.size) && (
                                            <span className="gptMsgFileSize">
                                              {formatFileSize(attachment.size)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            {message.content && (
                              <MarkdownContent content={message.content} />
                            )}
                            {message.timestamp && (
                              <span className="gptMsgTime">
                                {formatMessageTime(message.timestamp)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* ── Typing indicator, shown while the agent is composing a reply ── */}
                    {(sending || voiceStatus === 'Waiting for assistant') && (
                      <div className="gptMsg assistant gptTypingRow" aria-label="Agent is typing">
                        <div className="gptMsgAvatar">
                          <Briefcase size={13} />
                        </div>
                        <div className="gptMsgContent gptTypingBubble">
                          <span className="gptTypingDot" />
                          <span className="gptTypingDot" />
                          <span className="gptTypingDot" />
                        </div>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
            </div>

            <div className="gptInputZone">
              <div className="gptInputInner">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={
                    supportsVision
                      ? `${IMAGE_ACCEPT},${DOCUMENT_ACCEPT}`
                      : DOCUMENT_ACCEPT
                  }
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                {attachedFiles.length > 0 && (
                  <div className="gptAttachChips">
                    {attachedFiles.map((file, index) => (
                      <span key={index} className="gptAttachChip">
                        <Paperclip size={11} />
                        {file.name}
                        <button
                          type="button"
                          className="gptAttachChipRemove"
                          onClick={() => handleRemoveFile(index)}
                          aria-label={`Remove ${file.name}`}
                        >
                          <X size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <form className="gptInputCard" onSubmit={handleSend}>
                  <textarea
                    className="gptInputTextarea"
                    rows={1}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        handleSend(event);
                      }
                    }}
                    placeholder={
                      selectedAssistant.supports_chat
                        ? 'Type here ...'
                        : 'Text chat is not enabled for this assistant.'
                    }
                    disabled={!selectedAssistant.supports_chat || sending}
                  />

                  <div className="gptInputActions">
                    <button
                      type="button"
                      className={`gptAttachBtn${attachedFiles.length > 0 ? ' has-files' : ''}`}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={!selectedAssistant.supports_chat || sending}
                      title={
                        supportsVision
                          ? 'Attach files'
                          : `Attach documents, ${VISION_UNSUPPORTED_MESSAGE}`
                      }
                    >
                      <Paperclip size={16} />
                    </button>
                    <button
                      type="button"
                      className={`gptInputVoiceBtn${voiceRecording ? ' recording' : ''}${voiceConnecting ? ' connecting' : ''}${micIsBlocked && !voiceRecording ? ' micBlocked' : ''}`}
                      onClick={voiceRecording ? handleStopRecording : handleStartVoice}
                      disabled={!selectedAssistant.supports_realtime || voiceConnecting}
                      title={
                        voiceRecording
                          ? 'Stop recording'
                          : micIsBlocked
                          ? 'Microphone blocked, tap for help enabling it'
                          : 'Start voice'
                      }
                    >
                      {voiceRecording ? (
                        <span className="gptMicWave" aria-hidden="true">
                          <span /><span /><span />
                        </span>
                      ) : micIsBlocked ? (
                        <MicOff size={16} />
                      ) : (
                        <Mic size={16} />
                      )}
                    </button>
                    <button
                      type="submit"
                      className="gptInputSendBtn"
                      disabled={!selectedAssistant.supports_chat || sending || (!draft.trim() && attachedFiles.length === 0)}
                      title="Send message"
                    >
                      <ArrowUp size={16} />
                    </button>
                  </div>
                </form>

                {voiceIsActive ? (
                  <p className="gptVoiceInputHint">
                    <Mic size={12} />
                    Voice is active, text input is paused
                  </p>
                ) : !supportsVision ? (
                  <p className="gptInputCaption">
                    {VISION_UNSUPPORTED_MESSAGE} Documents are still supported.
                  </p>
                ) : (
                  <p className="gptInputCaption">
                    Verify important information before acting on assistant responses.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageContainer
      loading={loading}
      loadingMessage="Loading business assistants..."
      title="Business Assistants"
      subtitle="Open a dedicated workspace for the assistants available to your organization."
    >
      <div className="baDirectory">
        <section className="baDirectoryIntro">
          <div className="baDirectoryIntroCopy">
            <span className="baDirectoryEyebrow">Workspace Directory</span>
            <h2>Choose a Business Assistant</h2>
            <p>
              Each assistant opens in its own dedicated chat workspace with text history
              and realtime voice.
            </p>
          </div>

          <div className="baDirectoryStats">
            <div className="baDirectoryStat">
              <strong>{assistantStats.total}</strong>
              <span>Available assistants</span>
            </div>
            <div className="baDirectoryStat">
              <strong>{assistantStats.chat}</strong>
              <span>Chat enabled</span>
            </div>
            <div className="baDirectoryStat">
              <strong>{assistantStats.voice}</strong>
              <span>Voice enabled</span>
            </div>
          </div>
        </section>

        {assistants.length === 0 ? (
          <section className="baDirectoryEmpty">
            <Briefcase size={34} />
            <h3>No business assistants available</h3>
            <p>
              Your organization has not published any business assistants yet. Contact
              your organization admin if you expected one here.
            </p>
          </section>
        ) : (
          <section className="baDirectoryList">
            {assistants.map((assistant) => (
              <article key={assistant.id} className="baDirectoryCard">
                <div className="baDirectoryCardHeader">
                  <div className="baDirectoryCardIcon">
                    <Briefcase size={20} />
                  </div>

                  <div className="baDirectoryCardHeading">
                    <h3>{assistant.name}</h3>
                    <p>
                      {assistant.greeting ||
                        assistant.preview ||
                        'Internal business assistant ready to help.'}
                    </p>
                  </div>
                </div>

                <div className="baDirectoryMeta">
                  {assistant.supports_chat && (
                    <span className="baPill">
                      <MessageSquare size={11} />
                      Chat
                    </span>
                  )}
                  {assistant.supports_realtime && (
                    <span className="baPill">
                      <Mic size={11} />
                      Voice
                    </span>
                  )}
                </div>

                <div className="baDirectoryFooter">
                  <span className="baDirectoryOwner">
                    {assistant.created_by_email
                      ? `Published by ${assistant.created_by_email}`
                      : 'Published for your organization'}
                  </span>

                  <button
                    type="button"
                    className="baDirectoryOpenBtn"
                    onClick={() => handleOpenAssistant(assistant.id)}
                  >
                    Open Workspace
                    <ChevronRight size={16} />
                  </button>
                </div>
              </article>
            ))}
          </section>
        )}
      </div>
    </PageContainer>
  );
};

export default BusinessAssistants;
